import { encryptTradePassword } from '@tiger/api-signature';
import { Button, Form } from 'antd';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { $Formutil, withForm } from 'react-antd-formutil';
import { ModalHandler } from 'components/Modal';
import OnetimeCode, { OnetimeCodeProps } from 'components/OnetimeCode';
import Toast from 'components/Toast';
import SMSCode, { ProcessTypes, VerifyCodeSendProps } from 'components/VerifyCodeSend';
import security from 'stores/security';
import require2FA from 'utils/require2FA';

import './style.scss';

export interface SecurityVerifyInfo {
    verify_code?: string;
    type: VerificationMethod['type'];
}

interface VerificationMethod {
    type: 'sms' | 'tradePassword';
    label: string;
}

interface SafetyVerificationProps extends Pick<VerifyCodeSendProps, 'errorHandle'> {
    $formutil: $Formutil<{
        password: string;
        verify_code: string;
        trade_password: string;
    }>;
    process: ProcessTypes; // 具体获取什么类型的邀请码
    tradePasswordEnabled?: boolean; // 是否可以使用交易密码验证
    onSubmit: (value: SecurityVerifyInfo) => void;
}

const verificationMethods: VerificationMethod[] = [
    {
        type: 'sms',
        label: __('Use SMS code')
    },
    {
        type: 'tradePassword',
        label: __('Use trading password')
    }
];

const SafetyVerification: React.FC<SafetyVerificationProps & ModalHandler> = observer(props => {
    const { telCode, phone } = security.currentUser?.userInfo || {};
    const { $formutil, close, errorHandle, tradePasswordEnabled, onSubmit } = props;
    const { $params, $setValues } = $formutil;
    const [method, setMethod] = useState<VerificationMethod['type']>(tradePasswordEnabled ? 'tradePassword' : 'sms');
    const [loading, setLoading] = useState(false);
    const [tradePasswordErrorLevel, setTradePasswordErrorLevel] = useState<OnetimeCodeProps['errorLevel']>('off');
    const [tradePasswordAutoFocus, setTradePasswordAutoFocus] = useState(true);

    const getTradeToken = async (otp?: string) => {
        try {
            setLoading(true);

            const params = {
                grant_type: 'trade_password',
                trade_password: encryptTradePassword($params.trade_password),
                otp_code: otp,
                pwd_api_ver: 'v2'
            };

            await security.getTradeToken(params);

            await onSubmit({
                type: method
            });

            close!();
        } catch (error) {
            const { error_code } = error;

            // 需要两步验证token
            if (error_code >= 11401 && error_code <= 11402) {
                // 验证码不正确
                if (error_code === 11402) {
                    Toast.show(error);
                }

                setTradePasswordAutoFocus(false);

                require2FA()
                    .then(code => {
                        setTradePasswordAutoFocus(true);
                        getTradeToken(code);
                    })
                    .catch(err => {
                        Toast.show(err);
                    });
            } else {
                Toast.show(error);

                $setValues({
                    trade_password: ''
                });
            }
        }

        setLoading(false);
    };

    const handleSubmit = async () => {
        const { $invalid, $batchState, $getFirstError, $params } = $formutil;

        // 点击提交按钮的时候才展示错误信息
        setTradePasswordErrorLevel(2);

        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });

            Toast.show($getFirstError());
        } else {
            const { verify_code } = $params;

            if (method === 'sms') {
                try {
                    await onSubmit({
                        verify_code,
                        type: method
                    });

                    close!();
                } catch (error) {
                    $setValues({
                        verify_code: ''
                    });

                    Toast.show(error);
                }
            } else {
                getTradeToken();
            }
        }
    };

    return (
        <div className="safety-verification-root">
            <h3 className="title">
                {method === 'tradePassword' ? __('Enter trading password') : __('Enter SMS code')}
            </h3>
            <Form noValidate>
                <div className="method-box">
                    {method === 'tradePassword' ? (
                        <OnetimeCode
                            name="trade_password"
                            $onFieldChange={value => {
                                if (value.length === 6) {
                                    getTradeToken();
                                }
                            }}
                            variant="line"
                            autoFocus={tradePasswordAutoFocus}
                            checker={value => value?.length === 6 || __('请输入交易密码')}
                            errorLevel={tradePasswordErrorLevel}
                        />
                    ) : (
                        <div className="phone-box">
                            <p className="d-flex flex-wrap phone">
                                <label>{__('User Phone Number:')}</label>
                                <span>{`+${telCode} ${phone}`}</span>
                            </p>
                            <SMSCode
                                sendBtnText={__('Send Code')}
                                process={props.process}
                                type="phone"
                                placeholder={__('Enter SMS Code')}
                                errorHandle={errorHandle}
                                logined
                            />
                        </div>
                    )}
                </div>
                <Button className="btn-important" block type="primary" onClick={handleSubmit} loading={loading}>
                    {__('Confirm')}
                </Button>
                {tradePasswordEnabled &&
                    verificationMethods
                        .filter(({ type }) => type !== method)
                        .map(({ type, label }) => (
                            <Button
                                block
                                size="large"
                                className="btn-change-method"
                                key={type}
                                onClick={() => {
                                    setMethod(type);
                                    setTradePasswordErrorLevel('off');
                                }}>
                                {label}
                            </Button>
                        ))}
            </Form>
        </div>
    );
});

export default withForm(SafetyVerification);
